/**
 * Types for Undo/Redo System
 *
 * Phase 4: Optimistic mutations with inverse operations.
 * Uses operation-based undo (not full state snapshots) for efficiency.
 */

import type { PlayBlock_Union, BlockReference, ReferenceType } from '@/types/blocks'

// ========== Mutation Types ==========

/**
 * All mutation types that can be undone/redone.
 * Maps to specific API operations.
 */
export type BlockMutationType =
	| 'create_block'
	| 'update_block'
	| 'delete_block'
	| 'create_reference'
	| 'delete_reference'
	| 'move_block'
	| 'reorder_blocks'
	| 'apply_formation'
	| 'remove_formation'
	| 'apply_concept'

// ========== Inverse Operation Data ==========

/**
 * Data needed to undo a create_block operation.
 * Inverse: delete the created block.
 */
export interface CreateBlockInverseData {
	type: 'create_block'
	/** ID of the created block (to delete on undo) */
	blockId: string
}

/**
 * Data needed to undo an update_block operation.
 * Inverse: restore original values.
 */
export interface UpdateBlockInverseData {
	type: 'update_block'
	/** ID of the block that was updated */
	blockId: string
	/** Original field values before the update */
	originalValues: Record<string, unknown>
}

/**
 * Data needed to undo a delete_block operation.
 * Inverse: recreate the block with all its children and references.
 */
export interface DeleteBlockInverseData {
	type: 'delete_block'
	/** Full block data to restore */
	block: PlayBlock_Union
	/** Child blocks that were also deleted */
	children: PlayBlock_Union[]
	/** References involving this block */
	references: BlockReference[]
}

/**
 * Data needed to undo a create_reference operation.
 * Inverse: delete the reference.
 */
export interface CreateReferenceInverseData {
	type: 'create_reference'
	/** ID of the created reference */
	referenceId: string
}

/**
 * Data needed to undo a delete_reference operation.
 * Inverse: recreate the reference.
 */
export interface DeleteReferenceInverseData {
	type: 'delete_reference'
	/** Source block ID */
	sourceBlockId: string
	/** Target block ID */
	targetBlockId: string
	/** Reference type */
	referenceType: ReferenceType
	/** Display options (JSONB) */
	displayOptions: Record<string, unknown>
	/** Display order */
	displayOrder: number
}

/**
 * Data needed to undo a move_block operation.
 * Inverse: move block back to original parent/position.
 */
export interface MoveBlockInverseData {
	type: 'move_block'
	/** ID of the block that was moved */
	blockId: string
	/** Original parent block ID (null for root) */
	originalParentBlockId: string | null
	/** Original display order */
	originalDisplayOrder: number
}

/**
 * Data needed to undo a reorder_blocks operation.
 * Inverse: restore original display orders.
 */
export interface ReorderBlocksInverseData {
	type: 'reorder_blocks'
	/** Parent block ID (or null for root-level reorder) */
	parentBlockId: string | null
	/** Map of block ID to original display order */
	originalOrders: Map<string, number>
}

/**
 * Data needed to undo an apply_formation operation.
 * Inverse: apply the previous formation (or null to remove).
 */
export interface ApplyFormationInverseData {
	type: 'apply_formation'
	/** ID of the play block */
	playBlockId: string
	/** ID of the previous formation (null if none) */
	formationBlockId: string | null | undefined
}

/**
 * Data needed to undo a remove_formation operation.
 * Inverse: re-apply the formation that was removed.
 * This is the same as ApplyFormationInverseData.
 */
export interface RemoveFormationInverseData {
	type: 'remove_formation'
	/** ID of the play block */
	playBlockId: string
}

/**
 * Data needed to undo a remove_concept operation.
 * Inverse: re-apply the concept that was removed.
 */
export interface ApplyConceptInverseData {
	type: 'apply_concept'
	/** ID of the play block */
	playBlockId: string
	/** ID of the concept to re-apply */
	conceptBlockId: string
}

/**
 * Union of all inverse data types.
 */
export type InverseOperationData =
	| CreateBlockInverseData
	| UpdateBlockInverseData
	| DeleteBlockInverseData
	| CreateReferenceInverseData
	| DeleteReferenceInverseData
	| MoveBlockInverseData
	| ReorderBlocksInverseData
	| ApplyFormationInverseData
	| RemoveFormationInverseData
	| ApplyConceptInverseData

// ========== Redo Operation Data ==========

/**
 * Data needed to redo a create_block operation.
 */
export interface CreateBlockRedoData {
	type: 'create_block'
	/** Full block data to recreate */
	block: PlayBlock_Union
	/** Child blocks to recreate */
	children: PlayBlock_Union[]
	/** References to recreate */
	references: BlockReference[]
}

/**
 * Data needed to redo an update_block operation.
 */
export interface UpdateBlockRedoData {
	type: 'update_block'
	/** ID of the block to update */
	blockId: string
	/** New values to apply */
	newValues: Record<string, unknown>
}

/**
 * Data needed to redo a delete_block operation.
 */
export interface DeleteBlockRedoData {
	type: 'delete_block'
	/** ID of the block to delete */
	blockId: string
}

/**
 * Data needed to redo a create_reference operation.
 */
export interface CreateReferenceRedoData {
	type: 'create_reference'
	/** Source block ID */
	sourceBlockId: string
	/** Target block ID */
	targetBlockId: string
	/** Reference type */
	referenceType: ReferenceType
	/** Display options */
	displayOptions: Record<string, unknown>
	/** Display order */
	displayOrder: number
}

/**
 * Data needed to redo a delete_reference operation.
 */
export interface DeleteReferenceRedoData {
	type: 'delete_reference'
	/** ID of the reference to delete */
	referenceId: string
}

/**
 * Data needed to redo a move_block operation.
 */
export interface MoveBlockRedoData {
	type: 'move_block'
	/** ID of the block to move */
	blockId: string
	/** New parent block ID */
	newParentBlockId: string | null
	/** New display order */
	newDisplayOrder: number
}

/**
 * Data needed to redo a reorder_blocks operation.
 */
export interface ReorderBlocksRedoData {
	type: 'reorder_blocks'
	/** Parent block ID */
	parentBlockId: string | null
	/** Map of block ID to new display order */
	newOrders: Map<string, number>
}

/**
 * Data needed to redo an apply_formation operation.
 */
export interface ApplyFormationRedoData {
	type: 'apply_formation'
	/** ID of the play block */
	playBlockId: string
	/** ID of the formation to apply */
	formationBlockId: string
}

/**
 * Data needed to redo a remove_formation operation.
 */
export interface RemoveFormationRedoData {
	type: 'remove_formation'
	/** ID of the play block */
	playBlockId: string
}

/**
 * Data needed to redo an apply_concept operation.
 */
export interface ApplyConceptRedoData {
	type: 'apply_concept'
	/** ID of the play block */
	playBlockId: string
	/** ID of the concept to apply */
	conceptBlockId: string
}

/**
 * Union of all redo data types.
 */
export type RedoOperationData =
	| CreateBlockRedoData
	| UpdateBlockRedoData
	| DeleteBlockRedoData
	| CreateReferenceRedoData
	| DeleteReferenceRedoData
	| MoveBlockRedoData
	| ReorderBlocksRedoData
	| ApplyFormationRedoData
	| RemoveFormationRedoData
	| ApplyConceptRedoData

// ========== Undoable Operation ==========

/**
 * A single undoable operation stored in the undo stack.
 */
export interface UndoableOperation {
	/** Unique identifier for this operation */
	id: string
	/** Timestamp when operation was performed */
	timestamp: number
	/** Type of mutation */
	type: BlockMutationType
	/** Human-readable label for UI (e.g., "Add Player", "Move Block") */
	label: string
	/** Data needed to undo this operation */
	inverseData: InverseOperationData
	/** Data needed to redo this operation (after undo) */
	redoData: RedoOperationData
	/** Transaction ID for grouping related operations */
	transactionId?: string
}

// ========== Stack State ==========

/**
 * Current state of the undo/redo stacks.
 */
export interface UndoStackState {
	/** Operations that can be undone (most recent last) */
	past: UndoableOperation[]
	/** Operations that can be redone (most recent last) */
	future: UndoableOperation[]
}

// ========== Context Types ==========

/**
 * Actions available from the undo context.
 */
export interface UndoActions {
	/** Push a new operation onto the undo stack */
	pushOperation: (operation: UndoableOperation) => void
	/** Undo the most recent operation */
	undo: () => Promise<void>
	/** Redo the most recently undone operation */
	redo: () => Promise<void>
	/** Clear the future stack (called when new action performed) */
	clearFuture: () => void
	/** Start a transaction for grouping operations */
	startTransaction: (label: string) => string
	/** End a transaction */
	endTransaction: (transactionId: string) => void
	/** Clear all undo/redo history */
	clearAll: () => void
}

/**
 * Full undo context value.
 */
export interface UndoContextValue extends UndoActions {
	/** Whether undo is available */
	canUndo: boolean
	/** Whether redo is available */
	canRedo: boolean
	/** Label of next operation to undo (for UI) */
	undoLabel: string | null
	/** Label of next operation to redo (for UI) */
	redoLabel: string | null
	/** Current transaction ID (null if not in transaction) */
	currentTransactionId: string | null
}

// ========== Configuration ==========

/**
 * Maximum number of operations to keep in undo stack.
 */
export const MAX_UNDO_LEVELS = 50

/**
 * Generate a unique operation ID.
 */
export function generateOperationId(): string {
	return `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

/**
 * Generate a unique transaction ID.
 */
export function generateTransactionId(): string {
	return `tx_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}
